import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { requestResetPasswordSchema } from '@/features/auth/auth-model'
import { useRequestResetPassword } from '@/shared/hooks/api/auth'
import { AuthLayout } from '@/features/auth/auth-layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ZodIssue } from 'zod'

function issuesToMessage(issues: ZodIssue[], t: (key: string) => string): string {
  const first = issues[0]
  if (!first?.path[0]) {
    return t('forgotPassword.notifications.error')
  }
  const prefix = 'forgotPassword.fields'
  const path = String(first.path[0])
  if (path === 'email') {
    return t(`${prefix}.email.errors.invalid_string`)
  }
  return first.message
}

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<ZodIssue[]>([])
  const [success, setSuccess] = useState(false)
  const { mutateAsync: requestResetPassword, isPending } = useRequestResetPassword()

  const emailErrorMessage = errors.length > 0 ? issuesToMessage(errors, t) : undefined

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setErrors([])
      setSuccess(false)

      const validationResult = requestResetPasswordSchema.safeParse({ email })

      if (!validationResult.success) {
        setErrors(validationResult.error.issues)
      } else {
        try {
          await requestResetPassword({ email })
          setSuccess(true)
          toast.success(t('forgotPassword.notifications.success'))
        } catch (error) {
          toast.error(t('forgotPassword.notifications.error'))
          console.error(error)
        }
      }
    },
    [email, requestResetPassword, t]
  )

  return (
    <AuthLayout>
      <Card className="w-full max-w-md gap-4">
        <CardHeader className="space-y-2">
          <CardTitle>{t('forgotPassword.title')}</CardTitle>
          <CardDescription>{t('forgotPassword.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            {success && (
              <Alert variant="success">
                <AlertDescription className="whitespace-pre-line">
                  {t('forgotPassword.success')}
                </AlertDescription>
              </Alert>
            )}
            <div className="grid gap-2">
              <Label htmlFor="forgot-email">{t('forgotPassword.fields.email.label')}</Label>
              <Input
                id="forgot-email"
                type="email"
                value={email}
                disabled={success}
                aria-invalid={emailErrorMessage ? true : undefined}
                aria-describedby={emailErrorMessage ? 'forgot-email-error' : undefined}
                onChange={event => {
                  setEmail(event.target.value)
                  if (errors.length > 0) {
                    setErrors([])
                  }
                }}
                autoComplete="email"
              />
              {emailErrorMessage ? (
                <p id="forgot-email-error" className="text-sm text-destructive" role="alert">
                  {emailErrorMessage}
                </p>
              ) : null}
            </div>
            <Button type="submit" className="w-full" disabled={success || isPending}>
              {t('forgotPassword.actions.send')}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <Trans
                i18nKey="forgotPassword.backToLogin"
                components={{
                  Link: <Link className="text-primary underline underline-offset-4" to="/sign-in" />
                }}
              />
            </p>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
